'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    updateProfile,
    type User as FirebaseUser
} from 'firebase/auth';
import { doc, getDoc, setDoc, addDoc, collection, getDocs, writeBatch } from 'firebase/firestore';
import { auth, db } from '../firebase';

// Using consistent types with previous implementation where possible
export interface SignInInput {
    username: string; // Email
    password: string;
}

export interface SignUpInput {
    username: string;
    password: string;
    options?: {
        userAttributes?: {
            email: string;
            name: string;
        }
    }
}

export interface ConfirmSignUpInput {
    username: string;
    confirmationCode: string;
}

interface User {
    email: string;
    isPro: boolean;
    userId: string;
    displayName?: string;
    apiKey?: string;
    credits?: number | string; // number or 'UNLIMITED'
    subscriptionEnd?: Date;
    planType?: 'credit_pack' | 'payg'; // Credit pack or pay-as-you-go
    planName?: string;
    maxCredits?: number;
    lastLogReset?: Date;
    billing?: {
        currentBalance: number;
        totalApiCalls: number;
        totalCost: number;
        billingCycle: string;
        lastApiCall: any;
    };
}

interface PaymentData {
    razorpay_payment_id: string;
    amount: number;
    currency: string;
    credits: number;
    planName: string;
    validity?: 'month' | 'year' | 'once' | 'lifetime'; // New field to determining expiry
    planType?: 'credit_pack' | 'payg'; // Track billing type
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    login: (params: SignInInput) => Promise<void>;
    signup: (params: SignUpInput) => Promise<void>;
    confirmSignup: (params: ConfirmSignUpInput) => Promise<void>;
    logout: () => Promise<void>;
    isPro: boolean;
    upgradeToPro: () => void;
    recordPayment: (data: PaymentData) => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    updateUserProfile: (data: { displayName?: string }) => Promise<void>;
    regenerateApiKey: () => Promise<void>;
    deleteApiKey: () => Promise<void>;
    settleBalance: (paymentId: string, amount: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEMO_MODE = false;

function generateApiKey(): string {
    const array = new Uint8Array(24);
    crypto.getRandomValues(array);
    const randomString = Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `sk_live_${randomString}`;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: any | null) => {
            if (firebaseUser) {
                await fetchUserData(firebaseUser);
            } else {
                setUser(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    async function fetchUserData(firebaseUser: any) {
        console.log("AuthContext: Fetching user data for", firebaseUser.uid);
        try {
            const userDocRef = doc(db, 'bgusers', firebaseUser.uid);

            const timeout = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error("Firestore operation timed out")), 8000)
            );

            const userDoc = await Promise.race([getDoc(userDocRef), timeout]) as any;

            let isPro = false;
            let displayName = firebaseUser.displayName || '';
            let apiKey: string | undefined;

            let credits: number | string = 0;
            let subscriptionEnd: Date | undefined;
            let planType: 'credit_pack' | 'payg' | undefined;
            let planName: string | undefined;
            let maxCredits: number | undefined;
            let lastLogReset: Date | undefined;
            let billing: any | undefined;

            if (userDoc.exists()) {
                const data = userDoc.data();
                isPro = data.isPro || false;
                displayName = data.name || displayName;
                apiKey = data.apiKey;
                credits = data.credits || 0;
                planType = data.planType;
                planName = data.planName;
                maxCredits = data.maxCredits;
                billing = data.billing;
                if (data.subscriptionEnd) {
                    subscriptionEnd = data.subscriptionEnd.toDate();
                }
                if (data.lastLogReset) {
                    lastLogReset = data.lastLogReset.toDate();
                }
            }

            console.log("AuthContext: Setting user (Success)", { email: firebaseUser.email, isPro });
            setUser({
                email: firebaseUser.email || '',
                isPro,
                userId: firebaseUser.uid,
                displayName,
                apiKey,
                credits,
                subscriptionEnd,
                planType,
                planName,
                maxCredits,
                lastLogReset,
                billing
            });
        } catch (err) {
            console.warn("AuthContext: Firestore unreachable (using offline fallback)", err);
            setUser({
                email: firebaseUser.email || '',
                isPro: false,
                userId: firebaseUser.uid,
                // Do not generate dummy key in fallback
                credits: 0
            });
        }
    }

    const login = async ({ username, password }: SignInInput) => {
        await signInWithEmailAndPassword(auth, username, password);
    };

    const signup = async ({ username, password, options }: SignUpInput) => {
        let firebaseUser: any | null = null;

        try {
            const userCredential = await createUserWithEmailAndPassword(auth, username, password);
            firebaseUser = userCredential.user;
        } catch (error: any) {
            if (error.code === 'auth/email-already-in-use') {
                console.log("AuthContext: Email already exists, attempting login...");
                try {
                    const userCredential = await signInWithEmailAndPassword(auth, username, password);
                    firebaseUser = userCredential.user;
                } catch (loginError) {
                    console.error("AuthContext: Failed to login existing user during signup", loginError);
                    throw new Error("This email is already registered. Please login or check your password.");
                }
            } else {
                throw error;
            }
        }

        if (!firebaseUser) return;

        const name = options?.userAttributes?.name || 'User';
        const userDocRef = doc(db, 'bgusers', firebaseUser.uid);

        try {
            // Check if profile already exists to avoid overwriting or duplicate work
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
                console.log("AuthContext: User profile already exists in bgusers.");
                return;
            }

            console.log("AuthContext: Creating user profile for", firebaseUser.uid);

            const timeout = new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error("Firestore write timed out")), 15000)
            );

            await Promise.race([
                setDoc(userDocRef, {
                    email: username,
                    name: name,
                    isPro: false,
                    // No API Key on signup
                    createdAt: new Date()
                }),
                timeout
            ]);
            console.log("AuthContext: User profile created successfully.");
        } catch (e) {
            console.error("AuthContext: Firestore SDK failed - CRITICAL ERROR", {
                error: e,
                errorMessage: e instanceof Error ? e.message : String(e),
                userId: firebaseUser.uid
            });
            console.warn("⚠️ Make sure your Firestore database exists in Firebase Console!");
            console.warn("⚠️ Check Firestore security rules allow authenticated user writes!");
        }
    };

    const confirmSignup = async ({ username, confirmationCode }: ConfirmSignUpInput) => {
        // Firebase Auth (Email/Password) auto-confirms on signup
        console.log("Auto-confirming signup (Firebase handles this).");
        return;
    };

    const logout = async () => {
        await signOut(auth);
        setUser(null);
    };

    const upgradeToPro = async () => {
        if (user) {
            // Update local state immediately for UI feedback
            setUser(prev => prev ? { ...prev, isPro: true } : null);

            const userDocRef = doc(db, 'bgusers', user.userId);

            try {
                const timeout = new Promise<never>((_, reject) =>
                    setTimeout(() => reject(new Error("Firestore update timed out")), 15000)
                );

                await Promise.race([
                    setDoc(userDocRef, { isPro: true }, { merge: true }),
                    timeout
                ]);
                console.log("AuthContext: Upgraded to Pro successfully.");
            } catch (e) {
                console.warn("AuthContext: Failed to upgrade user to Pro", e);
                alert("Your upgrade was successful locally, but we couldn't save it to the server. Please check your internet connection.");
            }
        }
    };

    const recordPayment = async (data: PaymentData) => {
        if (!user) return;

        try {
            // Logic for expiry
            const now = new Date();
            let expiryDate = new Date(now);

            // Determine plan type based on validity
            // planType is what defines "behavior"
            const planType: 'credit_pack' | 'payg' = data.validity === 'once' ? 'credit_pack' : 'payg';
            const maxCredits = data.validity === 'once' ? data.credits : undefined;

            // Only calculate expiry for PAYG plans, not credit packs or lifetime
            if (planType === 'payg' && data.validity !== 'lifetime') {
                if (data.validity === 'year') {
                    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
                } else {
                    // Default to month (30 days)
                    expiryDate.setDate(expiryDate.getDate() + 30);
                }
            }

            // 1. Add specific payment record
            // Use 'payments' subcollection for this user in 'bgusers'
            const paymentRecord: any = {
                ...data,
                date: new Date(),
                userId: user.userId,
            };

            // Only add expiryDate for PAYG plans (excluding lifetime)
            if (planType === 'payg' && data.validity !== 'lifetime') {
                paymentRecord.expiryDate = expiryDate;
            }

            await addDoc(collection(db, 'bgusers', user.userId, 'payments'), paymentRecord);

            // 2. Update user profile (credits + apiKey if missing)
            const userRef = doc(db, 'bgusers', user.userId);
            let newCredits: number | string = 0;

            // STRICT RESET LOGIC
            // Credit Pack: Strictly set to pack amount. No accumulation.
            // PAYG: Strictly set to UNLIMITED.

            if (data.credits > 900000000) {
                newCredits = 'UNLIMITED';
            } else if (data.validity === 'once') {
                // Credit pack purchase/refill = Strict Reset
                newCredits = data.credits;
            } else {
                // PAYG plan
                newCredits = 'UNLIMITED';
            }

            // Mark the timestamp used to hide old logs (keep this as backup)
            const lastLogReset = new Date();

            // PHYSICAL DELETE of old logs
            try {
                const usageRef = collection(db, 'bgusers', user.userId, 'api_usage');
                const snapshot = await getDocs(usageRef);
                const batch = writeBatch(db);
                let count = 0;

                snapshot.docs.forEach((doc: any) => {
                    batch.delete(doc.ref);
                    count++;
                });

                if (count > 0) {
                    await batch.commit();
                    console.log(`AuthContext: Deleted ${count} old usage logs.`);
                }
            } catch (delError) {
                console.error("AuthContext: Failed to delete old logs", delError);
                // Non-critical, continue
            }

            const updates: any = {
                credits: newCredits,
                isPro: true,
                planType: planType,
                planName: data.planName,
                lastLogReset: lastLogReset,
            };

            // Store maxCredits for credit pack users
            if (maxCredits) {
                updates.maxCredits = maxCredits;
            }

            // Only set subscriptionEnd for PAYG plans (excluding lifetime)
            if (planType === 'payg' && data.validity !== 'lifetime') {
                updates.subscriptionEnd = expiryDate;
            }

            // Clear subscriptionEnd if switching TO credit pack from PAYG? 
            // Often safer to not delete it, but `planType` dictates behavior anyway.

            // If they don't have an API key yet, give them one
            if (!user.apiKey) {
                updates.apiKey = generateApiKey();
            }

            // Reset billing counters on any plan purchase/refill
            const nowCycle = new Date();
            const billingCycle = `${nowCycle.getFullYear()}-${String(nowCycle.getMonth() + 1).padStart(2, '0')}`;

            updates.billing = {
                currentBalance: 0,
                totalApiCalls: 0,
                totalCost: 0,
                billingCycle: billingCycle,
            };

            await setDoc(userRef, updates, { merge: true });

            // 3. Update local state
            setUser(prev => prev ? {
                ...prev,
                credits: newCredits,
                isPro: true,
                apiKey: prev.apiKey || updates.apiKey,
                subscriptionEnd: (planType === 'payg' && data.validity !== 'lifetime') ? expiryDate : undefined,
                planType: planType,
                maxCredits: maxCredits,
                lastLogReset: lastLogReset
            } : null);

            console.log("AuthContext: Payment recorded, credits strict-reset, logs hidden.");
        } catch (error) {
            console.error("AuthContext: Failed to record payment", error);
            throw error;
        }
    };

    const regenerateApiKey = async () => {
        if (!user) return;
        try {
            const newKey = generateApiKey();
            const userRef = doc(db, 'bgusers', user.userId);
            await setDoc(userRef, { apiKey: newKey }, { merge: true });
            setUser(prev => prev ? { ...prev, apiKey: newKey } : null);
            console.log("AuthContext: API Key regenerated successfully.");
        } catch (error) {
            console.error("AuthContext: Failed to regenerate API key", error);
            throw error;
        }
    };

    const deleteApiKey = async () => {
        if (!user) return;

        if (user.isPro) {
            alert("Active/Pro plan users cannot delete their API key. You can regenerate it instead.");
            return;
        }

        try {
            const userRef = doc(db, 'bgusers', user.userId);
            await setDoc(userRef, { apiKey: null }, { merge: true }); // Depending on Firestore rules/schema, might want specific deleteField() but null is often fine for simple flags
            setUser(prev => prev ? { ...prev, apiKey: undefined } : null);
            console.log("AuthContext: API Key deleted successfully.");
        } catch (error) {
            console.error("AuthContext: Failed to delete API key", error);
            throw error;
        }
    };

    const resetPassword = async (email: string) => {
        await sendPasswordResetEmail(auth, email);
    };

    const updateUserProfile = async (data: { displayName?: string }) => {
        if (!auth.currentUser || !user) throw new Error("No user logged in");

        if (data.displayName) {
            await updateProfile(auth.currentUser, {
                displayName: data.displayName
            });

            const userRef = doc(db, 'bgusers', user.userId);
            await setDoc(userRef, { name: data.displayName }, { merge: true });

            setUser(prev => prev ? { ...prev, displayName: data.displayName } : null);
        }
    };

    const settleBalance = async (paymentId: string, amount: number) => {
        if (!user?.userId) return;

        try {
            // 1. Record the settlement payment
            const paymentRecord = {
                razorpay_payment_id: paymentId,
                amount: amount,
                currency: 'USD',
                credits: 'BALANCE_SETTLEMENT',
                date: new Date(),
                receipt: paymentId,
                planName: 'Balance Settlement',
            };

            await addDoc(collection(db, 'bgusers', user.userId, 'payments'), paymentRecord);

            // 2. Clear the balance
            const userRef = doc(db, 'bgusers', user.userId);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
                const userData = userSnap.data();
                const billing = userData.billing || {};

                await setDoc(userRef, {
                    billing: {
                        ...billing,
                        currentBalance: 0,
                        totalCost: (billing.totalCost || 0) + amount
                    }
                }, { merge: true });

                // Update local state
                setUser(prev => {
                    if (!prev) return null;
                    const prevBilling = prev.billing || {
                        currentBalance: 0,
                        totalApiCalls: 0,
                        totalCost: 0,
                        billingCycle: '',
                        lastApiCall: null
                    };
                    return {
                        ...prev,
                        billing: {
                            ...prevBilling,
                            currentBalance: 0,
                            totalCost: (prevBilling.totalCost || 0) + amount
                        }
                    };
                });
            }
        } catch (error) {
            console.error("AuthContext: Failed to settle balance", error);
            throw error;
        }
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            login,
            signup,
            confirmSignup,
            logout,
            isPro: user?.isPro || false,
            upgradeToPro,
            recordPayment,
            resetPassword,
            updateUserProfile,
            regenerateApiKey,
            deleteApiKey,
            settleBalance
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
